import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthedImage } from '@/components/AuthedImage';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Link } from '@/components/ui/link';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { categories as catApi, items as itemApi, locations as locApi, tags as tagApi } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Chip } from '@/components/ui/chip';
import { EmptyLocationsCta } from '@/components/EmptyLocationsCta';
import { Tag, type TagProps } from '@/components/ui/tag';
import type { ItemCondition, LocationNode } from '@/types/api';

/** How many of the top item-count categories get a fast-path chip. */
const MAX_CATEGORY_CHIPS = 5;
/** Placeholder pill count shown while categories are loading, to avoid layout shift. */
const CATEGORY_CHIP_SKELETON_COUNT = 4;

const CONDITION_TONE: Record<ItemCondition, NonNullable<TagProps['tone']>> = {
  NEW: 'success',
  LIKE_NEW: 'success',
  GOOD: 'success',
  FAIR: 'warning',
  POOR: 'danger',
  BROKEN: 'danger',
};

export function ItemsPage() {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState('');
  // Arriving from the location tree preselects that location. Both filters live in the
  // URL (shareable, survives refresh) and are updated independently so setting one never
  // clobbers the other.
  const [params, setParams] = useSearchParams();
  const locationId = params.get('locationId') ? Number(params.get('locationId')) : undefined;
  const categoryId = params.get('categoryId') ? Number(params.get('categoryId')) : undefined;
  const setUrlFilter = (key: 'locationId' | 'categoryId', id: number | undefined) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === undefined) next.delete(key);
        else next.set(key, String(id));
        return next;
      },
      { replace: true },
    );
  };
  const setLocationId = (id: number | undefined) => setUrlFilter('locationId', id);
  const setCategoryId = (id: number | undefined) => setUrlFilter('categoryId', id);

  // The grid has no scroll container of its own (see AppShell's <main>), so the whole
  // window scrolls. Without this, picking a filter while scrolled down into a long list
  // leaves the page stranded mid-scroll instead of showing the new results from the top.
  // Keyed on the filter values (not the click handlers) so it also fires on browser
  // back/forward, and skipped on first mount so deep-linking to a filtered URL doesn't
  // cause a spurious jump.
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [categoryId, locationId]);

  const search = useQuery({
    queryKey: ['items', { q, locationId, categoryId }],
    queryFn: () => itemApi.search({ q: q || undefined, locationId, categoryId, size: 50 }),
  });
  const locTree = useQuery({ queryKey: ['locations', 'tree'], queryFn: locApi.tree });
  const cats = useQuery({ queryKey: ['categories'], queryFn: catApi.list });
  const tagsQuery = useQuery({ queryKey: ['tags'], queryFn: tagApi.list });

  const flatLocations = (() => {
    const out: { id: number; label: string }[] = [];
    const walk = (nodes: LocationNode[], prefix: string) => {
      for (const n of nodes) {
        out.push({ id: n.id, label: prefix + n.name });
        walk(n.children, prefix + n.name + ' / ');
      }
    };
    walk(locTree.data ?? [], '');
    return out;
  })();

  // Fast-path chips: top categories by item count, excluding empty ones. Ties keep the
  // API's original order (Array#sort is stable), which is good enough since ordering
  // among equal counts isn't specified.
  const categoryChips = (cats.data ?? [])
    .filter((c) => c.itemCount > 0)
    .sort((a, b) => b.itemCount - a.itemCount)
    .slice(0, MAX_CATEGORY_CHIPS);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('items.title')}</h1>
        <Button asChild>
          <RouterLink to={locationId !== undefined ? `/items/new?locationId=${locationId}` : '/items/new'}>
            <Plus className="mr-2 h-4 w-4" /> {t('items.create')}
          </RouterLink>
        </Button>
      </div>
      {locTree.data && locTree.data.length === 0 && <EmptyLocationsCta />}
      {cats.isLoading ? (
        <div className="flex flex-wrap gap-2" data-testid="category-chip-skeleton">
          {Array.from({ length: CATEGORY_CHIP_SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="h-8 w-20 animate-pulse rounded-pill bg-surface-2" />
          ))}
        </div>
      ) : (
        categoryChips.length > 0 && (
          <div role="radiogroup" aria-label={t('items.category')} className="flex flex-wrap gap-2">
            <Chip
              role="radio"
              aria-checked={categoryId === undefined}
              selected={categoryId === undefined}
              onClick={() => setCategoryId(undefined)}
            >
              {t('common.all')}
            </Chip>
            {categoryChips.map((c) => (
              <Chip
                key={c.id}
                role="radio"
                aria-checked={categoryId === c.id}
                selected={categoryId === c.id}
                onClick={() => setCategoryId(c.id)}
              >
                {i18n.language?.startsWith('tr') ? c.nameTr : c.nameEn}
              </Chip>
            ))}
          </div>
        )
      )}
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[1fr_14rem_14rem]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select
            value={locationId ?? ''}
            onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : undefined)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t('items.location')} — {t('common.all')}</option>
            {flatLocations.map((l) => (<option key={l.id} value={l.id}>{l.label}</option>))}
          </select>
          <select
            value={categoryId ?? ''}
            onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : undefined)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t('items.category')} — {t('common.all')}</option>
            {(cats.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{i18n.language?.startsWith('tr') ? c.nameTr : c.nameEn}</option>
            ))}
          </select>
        </CardContent>
      </Card>

      {search.isLoading && <div className="text-sm text-muted-foreground">{t('common.loading')}</div>}
      {search.data && search.data.content.length === 0 && (
        <div className="rounded-md border p-12 text-center text-sm text-muted-foreground">{t('common.noData')}</div>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {(search.data?.content ?? []).map((it) => (
          <Link key={it.id} to={`/items/${it.id}`} intent="standalone" className="block h-auto w-full rounded-lg p-0">
            <Card className="overflow-hidden transition-shadow hover:shadow-md">
              {it.thumbnailPath ? (
                <AuthedImage src={it.thumbnailPath} className="h-36 w-full object-cover" />
              ) : (
                <div className="flex h-36 items-center justify-center bg-muted text-xs text-muted-foreground">
                  {t('items.photos')} —
                </div>
              )}
              <CardContent className="space-y-1 p-3">
                <div className="font-medium">{it.name}</div>
                <div className="text-xs text-muted-foreground">{it.locationName}</div>
                <div className="flex flex-wrap gap-1 pt-1">
                  {it.categoryNameTr && (
                    <Tag tone="neutral">
                      {i18n.language?.startsWith('tr') ? it.categoryNameTr : (it.categoryNameEn ?? it.categoryNameTr)}
                    </Tag>
                  )}
                  {it.condition && (
                    <Tag tone={CONDITION_TONE[it.condition]} dot>
                      {t(`items.conditions.${it.condition}`)}
                    </Tag>
                  )}
                  {it.quantity > 1 && <Tag tone="qty">×{it.quantity}</Tag>}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        {t('items.tags')}: {(tagsQuery.data ?? []).map((tg) => tg.name).join(', ') || '—'}
      </div>
    </div>
  );
}
